// Three.js geometry for scene nodes, built from stereo-render's tessellation
// (via WASM on the main thread) so the editor, the browser depth pass and the
// server rasterizer all use identical triangles.

import * as THREE from 'three';
import init, { shapeTriangles } from '../../wasm/pkg/stereo_wasm.js';
import { base64ToF32, type SceneDesc, type Shape } from './types';

let ready: Promise<unknown> | null = null;

/** Initialises the main-thread WASM instance (geometry only). */
export function initGeometry(): Promise<unknown> {
  return (ready ??= init());
}

const cache = new Map<string, Float32Array>();

export function trianglesFor(shape: Shape, scene: SceneDesc): Float32Array {
  if (shape.kind === 'group') return new Float32Array(0);
  if (shape.kind === 'mesh') {
    const m = scene.meshes.find((x) => x.id === shape.mesh);
    return m ? base64ToF32(m.positions) : new Float32Array(0);
  }
  const key = JSON.stringify(shape);
  let t = cache.get(key);
  if (!t) {
    t = shapeTriangles(key);
    if (cache.size > 64) cache.clear();
    cache.set(key, t);
  }
  return t;
}

export function geometryFor(shape: Shape, scene: SceneDesc): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(trianglesFor(shape, scene), 3));
  g.computeVertexNormals();
  g.computeBoundingSphere();
  return g;
}
